import React, { useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import Footer from '../components/Footer';
import SharedNavbar from '../components/SharedNavbar';
import './FounderGuidePage.css';
import "../App.css";

const FounderGuidePage = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const guides = [
        {
            category: "Getting Started",
            subtitle: "Build clarity before you build anything else.",
            items: [
                {
                    title: "Create Your First Moonshot",
                    description: "Turn a vague ambition into a working strategic direction.",
                    time: "10 minutes",
                    outcome: "First Moonshot Map + baseline scores"
                },
                {
                    title: "Vision → Strategic Bets",
                    description: "Translate what you want into the few bets that actually matter.",
                    time: "15 minutes",
                    outcome: "Strategic Bets added to your Map"
                },
                {
                    title: "Clarity Score™ — What It Measures",
                    description: "Understand where confusion is hiding — and how to remove it.",
                    time: "10 minutes",
                    outcome: "Clear drivers behind your Clarity Score™"
                }
            ]
        },
        {
            category: "Market & Positioning",
            subtitle: "Stop guessing your market. Start bounding it.",
            items: [
                {
                    title: "TAM / SAM / SOM — Without the Fluff",
                    description: "Size your market in a way investors and operators actually trust.",
                    time: "15 minutes",
                    outcome: "Market layer added to your Map"
                },
                {
                    title: "Positioning: Category, Wedge, Why-Now",
                    description: "Answer “why you?” without sounding generic.",
                    time: "15 minutes",
                    outcome: "Positioning clarity with evidence flags"
                },
                {
                    title: "Competitive Landscape",
                    description: "See what users actually compare you to — not what you wish they did.",
                    time: "15 minutes",
                    outcome: "Competitive Map with real alternatives"
                }
            ]
        },
        {
            category: "Execution & Founder Rituals",
            subtitle: "Progress beats motivation. Rituals beat intentions.",
            items: [
                {
                    title: "The Weekly Clarity Ritual",
                    description: "A 15-minute reset to stay honest about progress.",
                    time: "15 minutes",
                    outcome: "Updated Map + trend signals"
                },
                {
                    title: "Assumptions → Tests → Evidence",
                    description: "Replace opinions with proof, one assumption at a time.",
                    time: "20 minutes",
                    outcome: "Evidence-linked assumptions"
                },
                {
                    title: "Milestones Investors Believe",
                    description: "Design milestones that signal readiness — not hope.",
                    time: "15 minutes",
                    outcome: "Credible milestone track"
                }
            ]
        },
        {
            category: "Fundraising Readiness",
            subtitle: "Before you pitch, know what will break the pitch.",
            items: [
                {
                    title: "Reality Check™ by Stage",
                    description: "Understand what “good progress” actually looks like for you.",
                    time: "10 minutes",
                    outcome: "Reality Check™ score with gap indicators"
                },
                {
                    title: "Deck → Map",
                    description: "Convert a pitch deck into an execution-ready roadmap.",
                    time: "20 minutes",
                    outcome: "Investor-readable Map"
                },
                {
                    title: "Common Red Flags",
                    description: "See risks before a human points them out.",
                    time: "15 minutes",
                    outcome: "Flagged issues + fix paths"
                }
            ]
        },
        {
            category: "Product & GTM",
            subtitle: "Distribution clarity before product complexity.",
            items: [
                {
                    title: "GTM Levers That Matter First",
                    description: "Identify your real growth constraint.",
                    time: "15 minutes",
                    outcome: "Prioritized GTM levers"
                },
                {
                    title: "Early-Stage Pricing Basics",
                    description: "Price for learning, not perfection.",
                    time: "10 minutes",
                    outcome: "Pricing assumptions mapped to evidence"
                },
                {
                    title: "Your First 10 Customers",
                    description: "From outreach to pipeline — mapped, not imagined.",
                    time: "20 minutes",
                    outcome: "Early pipeline Map + scripts"
                }
            ]
        }
    ];

    return (
        <div className="founder-guide-page">
            {/* --- Navigation --- */}
            <SharedNavbar activePage="guides" />

            {/* --- Main Content --- */}
            <div className="guide-main-wrapper">

                {/* Hero Section */}
                <div className="guide-hero-section">
                    <span className="hero-badge">The Playbooks</span>
                    <h1>Founder Guides</h1>
                    <p className="hero-subtitle">
                        Short, focused playbooks to turn ambition into execution.<br />
                        MoonshotMap Founder Guides are not articles. <strong>They are guided actions</strong> — each designed to produce a visible outcome inside your Map, Scores, or execution path.
                    </p>

                    <p className="hero-subtext">Every guide takes <strong>10–20 minutes</strong> and leaves you clearer than before.</p>
                </div>

                {/* What These Are Section */}
                <section className="guide-section light-bg">
                    <div className="content-container">
                        <div className="comparison-grid">
                            <div className="comparison-card positive">
                                <h3>What These Guides Are</h3>
                                <p className="comparison-subtitle">They help you think less and decide better.</p>
                                <ul className="check-list">
                                    <li>Structured, time-boxed, outcome-driven</li>
                                    <li>Designed for founders operating under uncertainty</li>
                                    <li>Built to update your Map, not just your understanding</li>
                                </ul>
                            </div>
                            <div className="comparison-card negative">
                                <h3>What They Are Not</h3>
                                <p className="comparison-subtitle">No fluff, no filler.</p>
                                <ul className="x-list">
                                    <li>Blog posts</li>
                                    <li>Generic startup advice</li>
                                    <li>One-size-fits-all frameworks</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* How It Works (Process) */}
                <section className="guide-section">
                    <div className="content-container">
                        <div className="section-header">
                            <h2>How a Guide Works</h2>
                            <p>Each guide follows the same simple loop</p>
                        </div>

                        <div className="steps-flow">
                            <div className="flow-step">
                                <span className="step-badge-number">1</span>
                                <h3>Focused Question</h3>
                                <p>You start with a single, high-impact question.</p>
                            </div>
                            <div className="flow-connector"></div>
                            <div className="flow-step">
                                <span className="step-badge-number">2</span>
                                <h3>Guided Flow</h3>
                                <p>MoonshotMap walks you through a short guided flow.</p>
                            </div>
                            <div className="flow-connector"></div>
                            <div className="flow-step">
                                <span className="step-badge-number">3</span>
                                <h3>Real-time Update</h3>
                                <p>Your Map, Scores, or priorities update in real time.</p>
                            </div>
                        </div>

                        {/* Additional Info from text */}
                        <div className="guide-info-strip">
                            <p>Every guide shows upfront:</p>
                            <div className="strip-pills">
                                <span>⏱ Time required (10–20 min)</span>
                                <span>🚀 What it improves</span>
                                <span>🎁 What you’ll get</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Guide Library */}
                <section className="guide-library">
                    <div className="content-container">
                        {guides.map((category, index) => (
                            <div key={index} className="category-block">
                                <div className="category-header">
                                    <h2>{category.category}</h2>
                                    <p>{category.subtitle}</p>
                                </div>
                                <div className="guides-grid">
                                    {category.items.map((item, i) => (
                                        <div key={i} className="guide-card">
                                            {/* Button Removed as requested */}
                                            <div className="guide-card-content">
                                                <h3>{item.title}</h3>
                                                <p className="guide-desc">{item.description}</p>
                                            </div>

                                            <div className="guide-meta-tags">
                                                <span className="meta-tag">⏱ {item.time}</span>
                                                <span className="meta-tag outcome">→ {item.outcome}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Bottom CTA (Restructured exactly as requested) */}
                <section className="guide-cta-section">
                    <div className="content-container">
                        <div className="cta-box-split">

                            {/* Left Side: Not Sure Where to Start */}
                            <div className="cta-col">
                                <h2>Not Sure Where to Start?</h2>
                                <p className="cta-text">If you’re new to MoonshotMap, start here:</p>
                                <ul className="cta-list-bullets">
                                    <li>Create Your First Moonshot</li>
                                    <li>Vision → Strategic Bets</li>
                                    <li>The Weekly Clarity Ritual</li>
                                </ul>
                                <p className="cta-note">This loop alone is enough to replace guessing with momentum.</p>
                            </div>

                            {/* Right Side: Why These Exist */}
                            <div className="cta-col dark-mode-panel">
                                <h3>Why These Guides Exist</h3>
                                <p className="cta-text">Founders don’t fail from lack of information. They fail from unresolved ambiguity.</p>
                                <p className="cta-text"><strong>MoonshotMap Guides exist to help you:</strong></p>
                                <ul className="cta-check-list">
                                    <li>See what matters now</li>
                                    <li>Ignore what doesn’t</li>
                                    <li>Move forward with confidence</li>
                                </ul>
                                <button className="cta-primary big-btn" style={{ marginTop: "24px" }}>Start Your First Guide →</button>
                            </div>

                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </div>
    );
};

export default FounderGuidePage;